import React, { useContext, createContext, useState, useMemo, useEffect, useCallback } from 'react'

import useTransactionsFiltersDrawer from 'pages/transactions/hooks/useTransactionsFiltersDrawer'

import { CustomFieldType } from 'api/types/customFieldsApiTypeSchema'
import { useOrg } from 'contexts/OrgContext'
import { useGetCustomFields } from 'hooks/data/customFieldsHooks'
import isEqual from 'utils/isEqual'

import { AssetType } from './assetTypes'
import { BackendFilterOptions } from './backendFilterTypes'

export const defaultFilterState: BackendFilterOptions = {
  dateRange: [],
  token: [],
  category: [],
  type: [],
  source: [],
  wallet: '',
  memo: '',
  to: '',
  from: '',
  txn_id: '',
  transaction_func: [],
  source_type: [],
  asset_type: [],
  missing_price: false,
  range: ['', ''],
  syncStatus: [],
  voucher: [],
  pageSize: 25,
  pageNumber: 0,
  queryExecutionId: '',
  currentToken: '',
  select_custom_fields: [],
  number_custom_fields: [],
  string_custom_fields: [],
  source_tags: [],
  // undefined means using gc, empty string means using iceberg main,
  // a non-empty string means using the given version in iceberg
  version: undefined,
}

export const emptyFilterState: BackendFilterOptions = {
  dateRange: [],
  token: [],
  category: [],
  type: [],
  source: [],
  wallet: '',
  memo: '',
  to: '',
  from: '',
  txn_id: '',
  transaction_func: [],
  source_type: [],
  asset_type: [],
  missing_price: false,
  range: ['', ''],
  syncStatus: [],
  voucher: [],
  pageSize: 25,
  pageNumber: 0,
  queryExecutionId: '',
  currentToken: '',
  select_custom_fields: [],
  number_custom_fields: [],
  string_custom_fields: [],
  source_tags: [],
  version: undefined,
}

type BackendFiltersContext = {
  nextToken: string
  setNextToken: (token: string) => void
  filterState: BackendFilterOptions
  filterStateWithUrlFilters: BackendFilterOptions
  applyBackendFilters: (state: BackendFilterOptions) => void
  areUserFiltersInUse: boolean
  hasInitializedUrlFilters: boolean
}

export const BackendFiltersContext = createContext({
  nextToken: '',
  setNextToken: () => undefined,
  filterState: defaultFilterState,
  filterStateWithUrlFilters: defaultFilterState,
  applyBackendFilters: () => undefined,
  areUserFiltersInUse: true,
  hasInitializedUrlFilters: false,
} as BackendFiltersContext)

export function useBackendFilters() {
  return useContext(BackendFiltersContext)
}

type DateRange = [string | null | undefined, string | null | undefined]

export const BackendFiltersProvider = ({ children }: { children: React.ReactNode }) => {
  const [filterState, setFilterState] = useState<BackendFilterOptions>(defaultFilterState)
  const [hasInitializedUrlFilters, setHasInitializedUrlFilters] = useState(false)

  const { data: customFields } = useGetCustomFields()

  const { activeFilters: activeFiltersFromUrl } = useTransactionsFiltersDrawer()

  useEffect(() => {
    if (customFields && customFields.length > 0) {
      const selectCustomFields = customFields
        .filter((field) => field.type === CustomFieldType.Select)
        .map((field) => ({ id: field.id, value: [] }))

      const stringCustomFields = customFields
        .filter((field) => field.type === CustomFieldType.Text)
        .map((field) => ({ id: field.id, value: [] }))

      const numberCustomFields = customFields
        .filter((field) => field.type === CustomFieldType.Number)
        .map((field) => ({ id: field.id, value_min: '', value_max: '' }))

      // The transaction page has UI which may show a "Select all transactions" option
      // if some filters are currently in use. The dynamic custom field filters are not
      // available until loaded from the API, so the empty filter state must be updated
      // to match these dynamically loaded filters for the UI comparison logic to work.
      emptyFilterState.select_custom_fields = selectCustomFields
      emptyFilterState.string_custom_fields = stringCustomFields
      emptyFilterState.number_custom_fields = numberCustomFields

      setFilterState((prevState) => ({
        ...prevState,
        select_custom_fields: selectCustomFields,
        string_custom_fields: stringCustomFields,
        number_custom_fields: numberCustomFields,
      }))
    }
  }, [customFields])

  const [nextToken, setNextToken] = useState<string>('')
  const { org } = useOrg()

  const applyBackendFilters = useCallback(
    (newFilterState: BackendFilterOptions) => {
      if (!isEqual(newFilterState, filterState)) {
        setFilterState(newFilterState)
      }
    },
    [filterState]
  )

  const filterStateWithUrlFilters = useMemo(() => {
    const dateRange: DateRange = [
      activeFiltersFromUrl.dateRange.startDate ?? null,
      activeFiltersFromUrl.dateRange.endDate ?? null,
    ]

    const range: [string, string] = [
      activeFiltersFromUrl.amount_range_min || '',
      activeFiltersFromUrl.amount_range_max || '',
    ]

    return {
      ...filterState,
      ...activeFiltersFromUrl,
      dateRange,
      range: range,
      asset_type: activeFiltersFromUrl.asset_type as AssetType[],
    }
  }, [filterState, activeFiltersFromUrl])

  const areUserFiltersInUse = useMemo(() => {
    const { currentToken, queryExecutionId, pageNumber, pageSize, ...userFilters } = filterState
    const {
      currentToken: _defaultCurrentToken,
      queryExecutionId: _defaultQueryExecutionId,
      pageNumber: _defaultPageNumber,
      pageSize: _defaultPageSize,
      ...emptyUserFilters
    } = emptyFilterState

    // noticed that for both the DateRangeFilter and DatePicker, when we clear the fields they return [undefined, undefined] or [null, null] respectively
    // we want to assign an empty array instead so it can be correctly calculated if the filters are in use
    if (userFilters.dateRange.every((date) => date === undefined || date === null)) {
      userFilters.dateRange = []
    }

    return !isEqual(userFilters, emptyUserFilters)
  }, [filterState])

  useEffect(() => {
    if (Object.keys(activeFiltersFromUrl).length > 0) {
      setHasInitializedUrlFilters(true)
    }
  }, [activeFiltersFromUrl])

  const value = useMemo(
    () => ({
      nextToken,
      setNextToken,
      filterState,
      filterStateWithUrlFilters,
      applyBackendFilters,
      areUserFiltersInUse,
      hasInitializedUrlFilters,
    }),
    [
      applyBackendFilters,
      areUserFiltersInUse,
      filterState,
      filterStateWithUrlFilters,
      nextToken,
      hasInitializedUrlFilters,
    ]
  )

  useEffect(() => {
    setFilterState(defaultFilterState)
    setNextToken('')
  }, [org])

  return <BackendFiltersContext.Provider value={value}>{children}</BackendFiltersContext.Provider>
}
